import { PriceInput } from "@components/common/PriceInput";
import type {
  AutocompleteInputChangeReason,
  InputBaseComponentProps,
  SxProps,
  Theme,
} from "@mui/material";
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { DesktopDatePicker, DesktopDateTimePicker } from "@mui/x-date-pickers";
import { MuiTelInput } from "mui-tel-input";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  calendarDateToPickerDate,
  fromPickerDate,
  pickerDateToCalendarDate,
  toPickerDate,
} from "@/utils/picker-date";
import { useFieldContext } from "./form.context";

type InputType =
  | "TextField"
  | "PhoneField"
  | "DateField"
  | "DateTimeField"
  | "SelectField"
  | "RadioField"
  | "TextAreaField"
  | "Autocomplete"
  | "AsyncAutocomplete"
  | "PriceField";

type VariantType = "standard" | "outlined" | "filled";
type TextInputType = "text" | "email" | "password" | "number";

type Option = {
  label?: string;
  name?: string;
  value: string | number | boolean;
};

type AsyncAutocompleteOption = { value: string | number; label: string };

type Props = {
  label: string;
  type: InputType;
  isEditAllowed?: boolean;
  variant?: VariantType;
  inputType?: TextInputType;
  disablePast?: boolean;
  greaterThan?: string;
  options?: Option[];
  inputProps?: InputBaseComponentProps;
  sx?: SxProps<Theme>;
  /** Only for AsyncAutocomplete */
  asyncAutocompleteProps?: {
    loading?: boolean;
    noOptionsText?: string;
    inputValue?: string;
    onInputChange?: (
      event: React.SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => void;
    getOptionValue?: (option: AsyncAutocompleteOption) => string | number;
    initialOption?: AsyncAutocompleteOption | null;
    selectedOption?: AsyncAutocompleteOption | null;
    onOptionSelect?: (option: AsyncAutocompleteOption | null) => void;
  };
  /** Only for PriceField: min, max, step, suffix */
  priceFieldProps?: { min?: number; max?: number; step?: number; suffix?: string };
  stripSpaces?: boolean;
};

export function AlbInput({
  label,
  type,
  isEditAllowed = true,
  variant = "standard",
  inputType = "text",
  disablePast = false,
  options,
  greaterThan,
  inputProps,
  asyncAutocompleteProps,
  priceFieldProps,
  sx,
  stripSpaces = false,
}: Props) {
  const { t } = useTranslation();
  const field = useFieldContext<unknown>();

  const errors = field.state.meta.errors;
  const hasError = field.state.meta.isTouched && errors.length > 0;
  const errorMsg = hasError
    ? typeof errors[0] === "string"
      ? errors[0]
      : (errors[0] as { message?: string })?.message
    : undefined;

  const watchedGreaterThanValue = greaterThan ? field.form.getFieldValue(greaterThan) : undefined;

  const greaterThanValue = useMemo(() => {
    if (!greaterThan) return undefined;
    if (typeof watchedGreaterThanValue !== "string" && !(watchedGreaterThanValue instanceof Date)) {
      return undefined;
    }
    return (
      (type === "DateField"
        ? calendarDateToPickerDate(watchedGreaterThanValue)
        : toPickerDate(watchedGreaterThanValue)) ?? undefined
    );
  }, [greaterThan, watchedGreaterThanValue, type]);

  if (type === "Autocomplete") {
    return (
      <Autocomplete
        disablePortal
        options={options || []}
        fullWidth
        disabled={!isEditAllowed}
        onChange={(_, value) => field.handleChange(value)}
        value={
          options?.find(
            (option) =>
              typeof field.state.value === "object" &&
              field.state.value !== null &&
              "value" in field.state.value &&
              option.value === field.state.value.value
          ) ?? null
        }
        isOptionEqualToValue={(option, value) => String(option.value) === String(value?.value)}
        sx={sx}
        renderInput={(params) => (
          <TextField
            {...params}
            name={field.name}
            onBlur={field.handleBlur}
            label={label}
            variant={variant}
            error={Boolean(hasError)}
            helperText={errorMsg ? t(`common.form.errors.${errorMsg}`) : ""}
            InputLabelProps={(field.state.value as string)?.length ? { shrink: true } : {}}
          />
        )}
      />
    );
  }

  if (type === "AsyncAutocomplete") {
    const asyncOpts = (options || []).filter(
      (option): option is AsyncAutocompleteOption =>
        typeof option.label === "string" && typeof option.value !== "boolean"
    );
    const {
      loading = false,
      noOptionsText,
      inputValue = "",
      onInputChange,
      getOptionValue = (o: AsyncAutocompleteOption) => o.value,
      initialOption,
      selectedOption,
      onOptionSelect,
    } = asyncAutocompleteProps ?? {};
    const storedValue = field.state.value as string;
    const id = storedValue != null && storedValue !== "" ? Number(storedValue) : null;
    const fromOptions =
      id != null
        ? asyncOpts.find((o) => getOptionValue(o) === id || getOptionValue(o) === storedValue)
        : null;
    const fromSelected =
      id != null &&
      selectedOption &&
      (getOptionValue(selectedOption) === id || getOptionValue(selectedOption) === storedValue)
        ? selectedOption
        : null;
    const fromInitial =
      id != null &&
      initialOption &&
      (getOptionValue(initialOption) === id || getOptionValue(initialOption) === storedValue)
        ? initialOption
        : null;
    const resolvedValue = fromOptions ?? fromSelected ?? fromInitial ?? null;
    return (
      <Autocomplete
        disablePortal
        options={asyncOpts}
        fullWidth
        disabled={!isEditAllowed}
        value={resolvedValue}
        inputValue={inputValue}
        onInputChange={onInputChange}
        onChange={(_, value) => {
          onOptionSelect?.(value ?? null);
          field.handleChange(value != null ? getOptionValue(value) : null);
        }}
        loading={loading}
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, v) =>
          v != null && getOptionValue(option) === getOptionValue(v)
        }
        noOptionsText={noOptionsText}
        sx={sx}
        renderInput={(params) => (
          <TextField
            {...params}
            name={field.name}
            onBlur={field.handleBlur}
            label={label}
            variant={variant}
            error={Boolean(hasError)}
            helperText={errorMsg ? t(`common.form.errors.${errorMsg}`) : ""}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }

  if (["TextField", "TextAreaField"].includes(type)) {
    return (
      <TextField
        name={field.name}
        value={(field.state.value as string) ?? ""}
        onChange={(e) => {
          const value = stripSpaces ? e.target.value.replace(/\s/g, "") : e.target.value;
          field.handleChange(value);
        }}
        onBlur={field.handleBlur}
        sx={{ textTransform: "capitalize", ...sx } as SxProps<Theme>}
        error={Boolean(hasError)}
        helperText={errorMsg ? t(`common.form.errors.${errorMsg}`) : ""}
        fullWidth
        variant={variant}
        label={label}
        InputLabelProps={(field.state.value as string)?.length ? { shrink: true } : {}}
        disabled={!isEditAllowed}
        type={inputType}
        multiline={type === "TextAreaField"}
        InputProps={{
          style: { minHeight: type === "TextAreaField" ? "100px" : "auto" },
          inputProps,
        }}
      />
    );
  }

  if (type === "PhoneField") {
    return (
      <MuiTelInput
        name={field.name}
        value={(field.state.value as string) ?? ""}
        onChange={(v) => field.handleChange(v)}
        onBlur={field.handleBlur}
        sx={{ textTransform: "capitalize", ...sx } as SxProps<Theme>}
        label={label}
        error={Boolean(hasError)}
        defaultCountry="FR"
        disabled={!isEditAllowed}
        variant="standard"
        InputLabelProps={(field.state.value as string)?.length ? { shrink: true } : {}}
        helperText={errorMsg ? t(`common.form.errors.${errorMsg}`) : ""}
        fullWidth
      />
    );
  }

  if (type === "DateTimeField") {
    const dateValue = toPickerDate(field.state.value as string | Date | undefined);
    return (
      <DesktopDateTimePicker
        name={field.name}
        value={dateValue}
        onChange={(date) => {
          field.handleChange(fromPickerDate(date)?.toISOString());
        }}
        disablePast={disablePast}
        ampm={false}
        sx={{ width: "100%", ...sx }}
        label={label}
        views={["month", "day", "year", "hours", "minutes"]}
        slotProps={{
          textField: {
            onBlur: field.handleBlur,
            error: Boolean(hasError),
            helperText: errorMsg ? t(`common.form.errors.${errorMsg}`) : "",
            variant,
          },
        }}
        minDateTime={greaterThanValue}
        disabled={!isEditAllowed}
      />
    );
  }

  if (type === "DateField") {
    const dateValue = calendarDateToPickerDate(field.state.value as string | Date | undefined);
    return (
      <DesktopDatePicker
        name={field.name}
        value={dateValue}
        onChange={(date) => {
          field.handleChange(pickerDateToCalendarDate(date) ?? undefined);
        }}
        disablePast={disablePast}
        sx={{ width: "100%", ...sx }}
        label={label}
        views={["month", "day", "year"]}
        slotProps={{
          textField: {
            onBlur: field.handleBlur,
            error: Boolean(hasError),
            helperText: errorMsg ? t(`common.form.errors.${errorMsg}`) : "",
            variant,
          },
        }}
        minDate={greaterThanValue}
        disabled={!isEditAllowed}
      />
    );
  }

  if (type === "SelectField") {
    return (
      <FormControl disabled={!isEditAllowed} fullWidth error={Boolean(hasError)}>
        <InputLabel id={field.name} sx={{ textTransform: "capitalize" }}>
          {label}
        </InputLabel>
        <Select
          name={field.name}
          value={(field.state.value as string) ?? ""}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          labelId={field.name}
          label={label}
          sx={sx}
        >
          {options?.map((option) => (
            <MenuItem
              key={`${field.name}-${String(option.value)}`}
              value={typeof option.value === "boolean" ? String(option.value) : option.value}
            >
              {option.name ?? option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{errorMsg ? t(`common.form.errors.${errorMsg}`) : ""}</FormHelperText>
      </FormControl>
    );
  }

  if (type === "PriceField") {
    const numValue =
      typeof field.state.value === "number" && Number.isFinite(field.state.value)
        ? field.state.value
        : 0;
    return (
      <PriceInput
        value={numValue}
        onChange={(v) => field.handleChange(v)}
        disabled={!isEditAllowed}
        error={Boolean(hasError)}
        helperText={errorMsg ? t(`common.form.errors.${errorMsg}`) : ""}
        variant={variant}
        fullWidth
        sx={sx}
        {...priceFieldProps}
      />
    );
  }

  if (type === "RadioField") {
    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value === "true") {
        field.handleChange(true);
      } else if (value === "false") {
        field.handleChange(false);
      } else {
        field.handleChange(value);
      }
    };

    return (
      <FormControl disabled={!isEditAllowed} error={Boolean(hasError)} fullWidth sx={sx}>
        <FormLabel id={field.name} sx={{ textTransform: "capitalize" }}>
          {label}
        </FormLabel>
        <RadioGroup
          row
          value={String(field.state.value)}
          onChange={handleRadioChange}
          aria-labelledby={field.name}
        >
          <Stack direction="row" width="80%" justifyContent="space-between">
            {options?.map((option) => (
              <FormControlLabel
                key={`${field.name}-${String(option.value)}`}
                value={String(option.value)}
                control={<Radio />}
                label={option.name ?? option.label}
                sx={{ textTransform: "capitalize" }}
              />
            ))}
          </Stack>
        </RadioGroup>
        <FormHelperText>{errorMsg ? t(`common.form.errors.${errorMsg}`) : ""}</FormHelperText>
      </FormControl>
    );
  }

  return null;
}
